import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@common/components/Button';
import Icon from '@common/components/Icon';
import { AlertDialog } from '@common/index';
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styles from './BigCommerce.module.scss';
import cardStyles from './IntegrationCard.module.scss';

interface BigCommerceProps {
    onConnect?: () => void;
    onDisconnect?: () => void;
}

export default function BigCommerce({
    onConnect,
    onDisconnect
}: BigCommerceProps) {
    const { nangoIntegrations } = useAuth();
    const {
        bigCommerce,
        connectBigCommerce,
        disconnectBigCommerce
    } = nangoIntegrations;

    const { connected, data } = bigCommerce;
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [storeDomain, setStoreDomain] = useState('');
    const [searchParams, setSearchParams] = useSearchParams();

    // Check for store_hash in URL parameters (Stage 3 of auth flow)
    // We only use this to pre-fill the UI state if needed, but actual connection
    // must be triggered by user click to avoid browser popup blocking.
    const storeHashFromUrl = searchParams.get('store_hash');
    const accountUuidFromUrl = searchParams.get('account_uuid');

    /**
     * Extracts the BigCommerce store hash from a given URL or string.
     * Supports formats:
     * - https://store-xxxxxx.mybigcommerce.com/
     * - store-xxxxxx.mybigcommerce.com
     * - store-xxxxxx
     * 
     * @param url The input string to parse
     * @returns The extracted store hash or null if not found
     * 
     * @author TraeAI
     * @date 2026-01-03
     */
    const extractBigCommerceHash = (url: string): string | null => {
        try {
            // Remove leading @ symbol if present
            const cleanUrl = url.startsWith('@') ? url.slice(1) : url;

            // Add https:// if no protocol
            const urlWithProtocol = cleanUrl.startsWith('http') ? cleanUrl : `https://${cleanUrl}`;

            // Parse URL
            const urlObj = new URL(urlWithProtocol);

            // Check if it's a mybigcommerce.com domain
            if (!urlObj.hostname.endsWith('.mybigcommerce.com')) {
                return null;
            }

            // Extract store hash from subdomain (e.g. store-xxxxxx)
            const subdomain = urlObj.hostname.split('.')[0];
            if (subdomain.startsWith('store-')) {
                return subdomain.replace('store-', '');
            }
            return subdomain;
        } catch (err) {
            return null;
        }
    };

    /**
     * Handles input changes for the store domain field.
     * Automatically extracts the store hash if a full BigCommerce URL is pasted.
     * 
     * @param e The change event
     * 
     * @author TraeAI
     * @date 2026-01-03
     */
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        
        // Check if full BigCommerce URL was pasted, if so extract store hash
        let processedValue = inputValue;
        if (inputValue.includes('.mybigcommerce.com')) {
            const extractedHash = extractBigCommerceHash(inputValue);
            if (extractedHash) {
                processedValue = extractedHash;
            }
        }
        
        setStoreDomain(processedValue);
        // Clear error when user types
        if (error) setError('');
    };

    const handleConnectWithHash = async (hash: string) => {
        setError('');
        setLoading(true);
        try {
            // Stage 3: Pass store_hash and account_uuid to connect logic
            const connectParams: any = { store_hash: hash };
            if (accountUuidFromUrl) {
                connectParams.accountUuid = accountUuidFromUrl;
            }
            await connectBigCommerce(connectParams);
            
            // Clean up URL parameter
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('store_hash');
            newParams.delete('account_uuid');
            setSearchParams(newParams);
            
            if (typeof onConnect === 'function') {
                onConnect();
            }
        } catch (err: any) {
            setError(err.message || 'Connection failed');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // We do NOT auto-connect here anymore to avoid "Modal blocked" error.
        // Instead, we wait for the user to click the "Complete Connection" button.
        if (storeHashFromUrl && !connected) {
            // Optional: You could show a toast or highlight the button here
            console.log('Store hash detected, waiting for user confirmation to complete connection');
        }
    }, [storeHashFromUrl, connected]);

    const handleConnect = async () => {
        const hashFromUrl = searchParams.get('store_hash');

        // If we have a hash from URL, use it directly (Stage 3)
        if (hashFromUrl) {
            await handleConnectWithHash(hashFromUrl);
            return;
        }

        // Otherwise start normal flow (Stage 1)
        if (!storeDomain.trim()) {
            setError('Please enter your BigCommerce store domain');
            return;
        }

        setError('');
        setLoading(true);
        try {
            // Stage 1: Trigger connection flow with store_domain
            const fullDomain = `https://store-${storeDomain}.mybigcommerce.com/`;
            await connectBigCommerce({ store_domain: fullDomain });
            
            if (typeof onConnect === 'function') {
                onConnect();
            }
        } catch (err: any) {
            setError(err.message || 'Connection failed');
        } finally {
            setLoading(false);
        }
    };

    const handleDisconnect = async () => {
        setError('');
        setLoading(true);
        try {
            await disconnectBigCommerce();
            if (typeof onDisconnect === 'function') {
                onDisconnect();
            }
        } catch (err: any) {
            setError(`Disconnection failed: ${err.message || 'Unknown error'}`);
        } finally {
            setLoading(false);
        }
    };

    const handleDismissError = () => {
        setError('');
    };

    return (
        <div className={cardStyles.integrationCard}>
            <div className={cardStyles.cardHeader}>
                <Icon name="BigCommerce" size={24} />
                <h3 className={cardStyles.cardTitle}>BigCommerce</h3>
            </div>

            <p className={cardStyles.cardDescription}>
                Connect your BigCommerce store to sync orders, products, and customer data directly within Bustly.
            </p>

            {!connected && !storeHashFromUrl && (
                <div className={styles.urlInputSection}>
                    <div className={styles.inputGroup}>
                        <label className={styles.inputLabel}>
                            BigCommerce Store URL
                        </label>
                        <div className={`${styles.inputWrapper} ${error && !storeDomain ? styles.inputError : ''}`}>
                            <div className={styles.urlPrefix}>https://store-</div>
                            <input
                                type="text"
                                className={styles.input}
                                placeholder="your-store"
                                value={storeDomain}
                                onChange={handleInputChange}
                                disabled={loading}
                            />
                            <div className={styles.urlSuffix}>.mybigcommerce.com</div>
                        </div>
                    </div>
                    <Button
                        className={styles.connectButton}
                        onClick={handleConnect}
                        size="2"
                        variant="solid"
                        color="primary"
                        style={{
                            padding: '0px 40px', height: '48px', width: 'auto' 
                        }}
                        loading={loading}
                        disabled={loading}
                    >
                        {loading ? 'Connecting...' : 'Connect'}
                    </Button>
                </div>
            )}

            {!connected && storeHashFromUrl ? (
                <div className={styles.urlInputSection} style={{ justifyContent: 'center' }}>
                    <div style={{ marginBottom: '16px', textAlign: 'center' }}>
                        <p style={{ fontWeight: 'bold' }}>
                            Store detected:
                            {storeHashFromUrl}
                        </p>
                        <p>Please click the button below to complete the secure connection.</p>
                    </div>
                    <Button
                        className={styles.connectButton}
                        onClick={() => handleConnectWithHash(storeHashFromUrl)}
                        size="2"
                        variant="solid"
                        color="primary"
                        style={{
                            padding: '0px 40px', height: '48px', width: '100%' 
                        }}
                        loading={loading}
                        disabled={loading}
                    >
                        {loading ? 'Finalizing...' : 'Complete Connection'}
                    </Button>
                </div>
            ) : null}

            {error && !connected ? (
                <div className={styles.errorContainer}>
                    <p className={styles.errorText}>{error}</p>
                    <button
                        onClick={handleDismissError}
                        className={styles.dismissButton}
                        title="Dismiss error"
                    >
                        <X size={14} />
                    </button>
                </div>
            ) : null}

            {connected ? (
                <div className={styles.connectedSection}>
                    <div className={styles.shopInfo}>
                        <div className={styles.shopDetails}>
                            <div className={styles.shopHeader}>
                                <span className={styles.shopName}>Store URL:</span>
                                <div className={styles.shopUrl}>
                                    {data?.mappings?.[0]?.domain 
                                     || (data?.mappings?.[0]?.store_hash ? `https://store-${data.mappings[0].store_hash}.mybigcommerce.com` : 'Unknown Store')}
                                </div>
                            </div>
                        </div>
                        <div
                            style={{
                                display: 'flex', gap: 16, alignItems: 'center' 
                            }}
                        >
                            <span className={styles.shopStatus}>
                                <b style={{ fontSize: 20, marginRight: 4 }}>·</b>
                                Connected
                            </span>
                            <AlertDialog
                                title="Disconnect BigCommerce?"
                                subtitle="If you disconnect, Bustly will stop syncing your BigCommerce data. You can reconnect anytime."
                                confirmText="Disconnect"
                                onConfirm={() => handleDisconnect()}
                                cancelText="Cancel"
                            >
                                <Button
                                    className={styles.disconnectButton}
                                    size="2"
                                    variant="outline"
                                    color="gray"
                                    style={{ padding: '0px 40px' }}
                                    loading={loading}
                                >
                                    Disconnect
                                </Button>
                            </AlertDialog>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
